import React, { useState } from 'react';
import { CheckinStep, DeskShare, setDeskShare } from '../../../../api/stay-checkin-client';
import { StatusLabel } from '../../../../motion';
import { ErrorText } from '../../../onboarding/onboarding.styled';
import { Actions, Body, CheckRow, Primary, Secondary } from '../../../onboarding/stay/public-stay-page.styled';
import { t } from '../../../onboarding/stay/stay-strings';
import { Note, RulesText, StepHeading } from './guest-checkin.styled';

const L = 'en' as const;

export interface DeskShareAnswer {
  steps: CheckinStep[];
  completed_at: string | null;
  desk_share: DeskShare | null;
}

/**
 * SHARING WITH THE BUILDING'S FRONT DESK (Independent Stays plan IS7, IS7-D1).
 *
 * While the listing is linked, check-in cannot finish without this: the server's step,
 * never the page's. The words are the server's, and the log records their version and
 * hash. Agreed already: a plain line and "Stop sharing". After check-in is complete it
 * shows on the Done page as a request instead, because a finished check-in is not undone.
 */
export const DeskShareStep: React.FC<{
  token: string;
  share: DeskShare;
  onSaved: (out: DeskShareAnswer) => void;
  onError: (err: unknown) => string | null;
  /** On the Done page: a short card under the guide, not a step. */
  compact?: boolean;
}> = ({ token, share, onSaved, onError, compact = false }) => {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const send = async (decision: 'given' | 'withdrawn') => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      onSaved(await setDeskShare(token, decision, share.version));
      setAgree(false);
    } catch (err) {
      setProblem(onError(err));
    } finally {
      setBusy(false);
    }
  };

  if (share.given) {
    return (
      <div data-testid="desk-share" data-desk-share="given">
        <StepHeading>{t(L, 'deskShareTitle')}</StepHeading>
        <Note role="status">{t(L, 'deskShareGiven', { building: share.building_name })}</Note>
        {problem ? <ErrorText>{problem}</ErrorText> : null}
        <Actions>
          <Secondary type="button" onClick={() => send('withdrawn')} aria-busy={busy || undefined}>
            <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'deskShareStop'), busy: t(L, 'sending') }} />
          </Secondary>
        </Actions>
      </div>
    );
  }

  return (
    <div data-testid="desk-share" data-desk-share="asked">
      <StepHeading>{t(L, 'deskShareTitle')}</StepHeading>
      {compact ? <Body>{t(L, 'deskShareLater', { building: share.building_name })}</Body> : null}
      <Body>{t(L, 'deskShareBuilding', { building: share.building_name })}</Body>
      <RulesText>{share.text}</RulesText>
      <CheckRow htmlFor="desk-share-agree">
        <input id="desk-share-agree" type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />
        <span>{t(L, 'deskShareAgree')}</span>
      </CheckRow>
      {problem ? <ErrorText>{problem}</ErrorText> : null}
      <Actions>
        <Primary type="button" disabled={!agree} onClick={() => send('given')} aria-busy={busy || undefined}>
          <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'deskShareSave'), busy: t(L, 'sending') }} />
        </Primary>
      </Actions>
    </div>
  );
};
